import { Fragment } from 'react';
import { Row, Col, Form, Card } from 'react-bootstrap';
import { Formik } from 'formik';
import './UploadDocument.scss';
import GenericButton from '../../../Components/GenericButton/GenericButton';
import { useTranslation } from 'react-i18next';
import MultipleFileUpload from '../../../Components/fileupload/MultipleFileUpload';
import FileInfo from '../../../Components/fileupload/FileInfo';
import useUploadDocument from './hooks/useUploadDocument';
import AutoSuggestionSelectComponentAsync from '../common/auto-suggestion-select/AsyncSelect';
import AutoSuggestionSelectComponent from '../common/auto-suggestion-select/Select';
import SendDocumentSkeleton from './skeleton/SendDocumentSkeleton';
import FilterModal from '../common/filter-modal/FilterModal';
import UploadDocumentsOverlay from './UploadDocumentsOverlay';
import { Link } from 'react-router-dom';

const SendDocument = () => {
  const {
    files,
    filesLoader,
    title,
    sendDocumentFor,
    referenceNumber,
    docType,
    comments,
    schema,
    formRef,
    sendDocumentForOptionList,
    typeOptionList,
    loadingApiConfig,
    overlayRef,
    focusOnlyRef,
    isOpenOverlayPopup,
    formUploadStatus,
    fileProgress,
    fileUploadStatus,
    serverError,
    setFiles,
    setTitle,
    setReferenceNumber,
    setComments,
    reviewForm,
    submitData,
    setIsOpenOverlayPopup,
    uploadDone,
    retryUploadAllFiles,

    CustomSearchIcon,
    FormatOptionLabel,
    claimNumberText,
    claimNumberLoading,
    searchedClaimOptions,
    defaultClaimList,
    onSearch,
    browseFileSeactionFocus,
  } = useUploadDocument();

  const { t } = useTranslation();

  return (
    <div>
      <Card className="Upload-Document">
        <Formik
          validationSchema={schema}
          initialValues={{
            senddocumentfor: sendDocumentFor,
            title: title,
            comments: comments,
            type: typeOptionList[0],
            referenceNumber: referenceNumber,
            file: files,
          }}
          onSubmit={(values) => {
            submitData(values);
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            setFieldTouched,
            setFieldValue,
            values,
            touched,
            errors,
          }) => (
            <Form
              className="UploadFilesForm-Container"
              onSubmit={handleSubmit}
              ref={formRef}
            >
              {loadingApiConfig ? (
                <SendDocumentSkeleton />
              ) : (
                <>
                  <Form.Group className="form-block" controlId="UploadFiles">
                    <Row className="mt-4">
                      <Col md={6} lg={6} sm={6}>
                        <Form.Group controlId="senddocumentfor">
                          <Form.Label className="document-labels">
                            {t('member.upload_document.senddocumentfor')}
                            <span className="form-required" aria-hidden={true}>
                              *
                            </span>
                          </Form.Label>
                          <AutoSuggestionSelectComponent
                            options={sendDocumentForOptionList}
                            name="senddocumentfor"
                            components={{
                              IndicatorSeparator: () => null,
                            }}
                            value={sendDocumentFor}
                            onChange={(e) => {
                              const event = {
                                target: { name: 'senddocumentfor', value: e },
                              };
                              handleChange(event);
                            }}
                            onBlur={() => {
                              handleBlur({
                                target: { name: 'senddocumentfor' },
                              });
                            }}
                            fieldTouch={setFieldTouched}
                            isSearchable={false}
                            inputClassName={
                              touched.senddocumentfor && errors.senddocumentfor
                                ? 'invalid-error'
                                : ''
                            }
                            id="senddocumentfor"
                            labelText={'filter dropdown, senddocumentfor'}
                            isRequired={true}
                            placeholder={t(
                              'member.upload_document.senddocumentfor',
                            )}
                            isInvalid={
                              touched.senddocumentfor && errors.senddocumentfor
                            }
                            required={true}
                            valid={errors.senddocumentfor ? true : null}
                            describedby="err_senddocumentfor"
                          />
                          <span
                            className="invalid-error"
                            type="invalid"
                            aria-live="polite"
                            id="err_senddocumentfor'"
                            role="alert"
                          >
                            {touched.senddocumentfor && errors.senddocumentfor}
                          </span>
                        </Form.Group>
                      </Col>
                      <Col md={6} lg={6} sm={6}>
                        <Form.Group controlId="title">
                          <Form.Label className="document-labels">
                            {t('member.upload_document.title')}
                            <span className="form-required" aria-hidden={true}>
                              *
                            </span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="title"
                            placeholder=""
                            className="title-class"
                            value={values.title ?? ''}
                            spellCheck="off"
                            autoCorrect="off"
                            autoComplete="off"
                            autoFocus={false}
                            onBlur={(e) => {
                              handleBlur(e);
                              const storeTitle = e.currentTarget.value.trim();
                              setFieldValue('title', storeTitle);
                            }}
                            onChange={(e) => {
                              setFieldTouched('title');
                              handleChange(e);
                              setTitle(e.target.value);
                            }}
                            isInvalid={touched.title && errors.title}
                            aria-required={true}
                            aria-invalid={errors.title ? true : null}
                            aria-describedby="err_title"
                          />
                          <Form.Control.Feedback
                            type="invalid"
                            aria-live="polite"
                            id="err_title"
                            role="alert"
                          >
                            <span>{touched.title && errors.title}</span>
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={6} lg={6} sm={6}>
                        <Form.Group controlId="type">
                          <Form.Label className="document-labels">
                            {t('member.upload_document.type')}
                            <span className="form-required" aria-hidden={true}>
                              *
                            </span>
                          </Form.Label>
                          <AutoSuggestionSelectComponent
                            options={typeOptionList}
                            name="type"
                            components={{ IndicatorSeparator: () => null }}
                            value={docType}
                            onChange={(e) => {
                              const event = {
                                target: { name: 'type', value: e },
                              };
                              handleChange(event);
                            }}
                            onBlur={() => {
                              handleBlur({ target: { name: 'type' } });
                            }}
                            fieldTouch={setFieldTouched}
                            isSearchable={false}
                            inputClassName={
                              touched.type && errors.type ? 'invalid-error' : ''
                            }
                            id="type"
                            labelText={'filter dropdown, type'}
                            isRequired={true}
                            isInvalid={touched.type && errors.type}
                            required={true}
                            valid={errors.type ? true : null}
                            describedby="err_type"
                          />
                          <span
                            className="invalid-error"
                            type="invalid"
                            aria-live="polite"
                            id="err_type"
                            role="alert"
                          >
                            {touched.type && errors.type}
                          </span>
                        </Form.Group>
                      </Col>
                      {values?.type?.label === 'Claims' && (
                        <Col md={6} lg={6} sm={6}>
                          <Form.Group controlId="referenceNumber">
                            <Form.Label className="document-labels">
                              {t('member.upload_document.reference_number')}
                              <span
                                className="form-required"
                                aria-hidden={true}
                              >
                                *
                              </span>
                            </Form.Label>
                            <AutoSuggestionSelectComponent
                              placeholder="Example: CLM20230620001"
                              components={{
                                DropdownIndicator: CustomSearchIcon,
                                IndicatorSeparator: () => null,
                                Option: FormatOptionLabel,
                              }}
                              options={
                                claimNumberLoading
                                  ? []
                                  : claimNumberText
                                  ? searchedClaimOptions
                                  : defaultClaimList
                              }
                              onInputChange={(e) => onSearch(e)}
                              inputClassName="border-right-0"
                              isLoading={claimNumberLoading}
                              onChange={(e) => {
                                setReferenceNumber(e);
                                const event = {
                                  target: {
                                    name: 'referenceNumber',
                                    value: e?.value,
                                  },
                                };
                                handleChange(event);
                              }}
                              value={referenceNumber}
                              id="referenceNumber"
                              name="referenceNumber"
                              valid={
                                touched.referenceNumber &&
                                errors.referenceNumber
                                  ? true
                                  : null
                              }
                              noOptionsMessage={() =>
                                claimNumberLoading
                                  ? 'Loading...'
                                  : 'No Data Found'
                              }
                              describedby="err_referenceNumber"
                              labelText={t(
                                'member.price_comparison.procedure_container.health_procedure_dropdown_labelText',
                              )}
                            />
                            <span
                              className="invalid-error"
                              type="invalid"
                              aria-live="polite"
                              id="err_referenceNumber"
                              role="alert"
                            >
                              {touched.referenceNumber &&
                                errors.referenceNumber}
                            </span>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                  </Form.Group>

                  <Row className="w-90 mt-3">
                    <Col md={12} lg={12} sm={12}>
                      <MultipleFileUpload
                        className={
                          files.length <= 0 && touched.file && errors.file
                            ? 'errorbackground'
                            : ''
                        }
                        files={files}
                        setFiles={setFiles}
                        name="file"
                        setFieldValue={setFieldValue}
                        browseFileSeactionFocus={browseFileSeactionFocus}
                      />
                      {files && files.length > 0 ? (
                        ''
                      ) : (
                        <span className="sr-only">
                          {touched.file && errors.file}
                        </span>
                      )}
                    </Col>
                    <Col md={12} lg={6} sm={12}>
                      {files.length > 0 ? (
                        <div
                          className="sr-only"
                          role="heading"
                          aria-level={1}
                          tabIndex={0}
                        >
                          {`${files.length} file${
                            files.length > 1 ? 's' : ''
                          } selected to upload `}
                        </div>
                      ) : (
                        ''
                      )}
                      <div>
                        {files &&
                          files.map((item, index) => {
                            return (
                              <Fragment key={index}>
                                <FileInfo
                                  files={files}
                                  setFiles={setFiles}
                                  fileDetails={item}
                                  filesLoader={false}
                                  setFieldValue={setFieldValue}
                                  readFileSize={true}
                                  browseFileSeactionFocus={
                                    browseFileSeactionFocus
                                  }
                                />
                              </Fragment>
                            );
                          })}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mt-3 w-90">
                    <Col md={12} lg={12} sm={12}>
                      <Form.Group className="form-block" controlId="comments">
                        <Form.Label className="document-labels">
                          {t('member.upload_document.comment')}
                        </Form.Label>
                        <Form.Control
                          rows={4}
                          as="textarea"
                          className="comments"
                          spellCheck="off"
                          autoCorrect="off"
                          autoComplete="off"
                          name="comments"
                          autoFocus={false}
                          onBlur={(e) => {
                            handleBlur(e);
                            const storeComments = e.currentTarget.value.trim();
                            setFieldValue('comments', storeComments);
                          }}
                          onChange={(e) => {
                            setFieldTouched('comments');
                            handleChange(e);
                            setComments(e.target.value);
                          }}
                          value={comments ?? ''}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="sr-only">
                    <Col md={6} lg={6} sm={6}>
                      {Object.keys(errors).length > 0 && (
                        <button
                          tabIndex={-1}
                          className="reviewFormErrors"
                          onClick={() => reviewForm(Object.keys(errors).length)}
                        >
                          Fill Missing Form Data
                        </button>
                      )}
                    </Col>
                  </div>

                  <div className=" d-flex justify-content-between align-items-center mt-4 mb-4 button-container">
                    <div>
                      <Link
                        to={'/member/messages-documents-notifications'}
                        state={{
                          sendDocumentBackButton: true,
                          comingFrom: 'DashboardUploadIcon',
                        }}
                      >
                        <button
                          className="cancel-btn border-0 bg-transparent"
                          role="button"
                          aria-label="Click on cancel to go to documents list page"
                          tabIndex={0}
                        >
                          {t('member.upload_document.cancel')}
                        </button>
                      </Link>
                    </div>
                    <div>
                      <GenericButton
                        type="submit"
                        variant="primary"
                        tabIndex={0}
                        ariaLabel="Click send button to upload document information"
                        ref={overlayRef}
                        onKeyPress={(event) => {
                          if (event.key === 'Enter') {
                            setIsOpenOverlayPopup(true);
                          }
                        }}
                      >
                        {t('member.upload_document.send')}
                      </GenericButton>
                      <label
                        tabIndex={0}
                        ref={focusOnlyRef}
                        aria-hidden={true}
                        className="sr-only"
                      >
                        {' '}
                        For Focus
                      </label>
                    </div>
                  </div>

                  <FilterModal
                    isVisible={isOpenOverlayPopup}
                    titleLabel={'Send Documents'}
                    showCancel={true}
                    showCancelLabel={'Close'}
                    showApply={fileUploadStatus === 'failed' || serverError}
                    showApplyLabel={'Retry'}
                    showBackButton={false}
                    onHide={uploadDone}
                    applyFilterHandler={() =>
                      serverError ? submitData(values) : retryUploadAllFiles()
                    }
                  >
                    <UploadDocumentsOverlay
                      files={files}
                      setFiles={setFiles}
                      filesLoader={filesLoader}
                      setFieldValue={setFieldValue}
                      formUploadStatus={formUploadStatus}
                      fileProgress={fileProgress}
                      fileUploadStatus={fileUploadStatus}
                      serverError={serverError}
                      readFileSize={false}
                    />
                  </FilterModal>
                </>
              )}
            </Form>
          )}
        </Formik>
      </Card>
    </div>
  );
};

export default SendDocument;
